import React from 'react';
import type { SocialMediaContent } from '../../services/socialMediaContentService';
import './AdminComponents.css';

interface ViewContentModalProps {
  content: SocialMediaContent | null;
  onClose: () => void;
}

const ViewContentModal: React.FC<ViewContentModalProps> = ({ content, onClose }) => {
  if (!content) return null;

  const getPlatformIcon = (platform: string): string => {
    switch (platform) {
      case 'YOUTUBE': return '▶️';
      case 'TIKTOK': return '🎵';
      case 'INSTAGRAM': return '📸';
      case 'FACEBOOK': return '👥';
      case 'LINKEDIN': return '💼';
      default: return '🌐';
    }
  };

  const getStatusDisplay = (status: string): string => {
    switch (status) {
      case 'DRAFT': return 'Draft';
      case 'PENDING_APPROVAL': return 'Pending Approval';
      case 'APPROVED': return 'Approved';
      case 'PUBLISHED': return 'Published';
      case 'REJECTED': return 'Rejected';
      default: return status;
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'DRAFT': return 'status-badge draft';
      case 'PENDING_APPROVAL': return 'status-badge pending';
      case 'APPROVED': return 'status-badge approved';
      case 'PUBLISHED': return 'status-badge published';
      case 'REJECTED': return 'status-badge rejected';
      default: return 'status-badge';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content view-content-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Content Details</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="content-details">
            {/* Header Section */}
            <div className="content-header-section">
              <div className="content-title-section">
                <h3 className="content-title">{content.title}</h3>
                <div className="content-meta">
                  <span className="platform-badge">
                    {getPlatformIcon(content.platform)} {content.platform}
                  </span>
                  <span className={`status-badge ${getStatusBadgeClass(content.status)}`}>
                    {getStatusDisplay(content.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="content-section">
              <h4>Description</h4>
              <p className="content-description">{content.description}</p>
            </div>

            {/* Content Type */}
            <div className="content-section">
              <h4>Content Type</h4>
              <span className="type-badge">{content.contentType}</span>
            </div>

            {/* Tags */}
            {content.tags && (
              <div className="content-section">
                <h4>Tags</h4>
                <div className="content-tags">
                  {content.tags.split(',').map(tag => (
                    <span key={tag.trim()} className="tag">
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Media URL */}
            {content.mediaUrl && (
              <div className="content-section">
                <h4>Media URL</h4>
                <div className="media-url-container">
                  <a 
                    href={content.mediaUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="media-url"
                  >
                    {content.mediaUrl}
                  </a>
                </div>
              </div>
            )}

            {/* Schedule Information */}
            {content.publishDate && (
              <div className="content-section">
                <h4>Publish Date</h4>
                <p>{new Date(content.publishDate).toLocaleString()}</p>
              </div>
            )}

            {/* User Information */}
            <div className="content-section">
              <h4>Created By</h4>
              <p>User ID: {content.createdBy || 'Unknown'}</p>
              <p>Created: {new Date(content.createdAt).toLocaleString()}</p>
              {content.updatedAt && content.updatedAt !== content.createdAt && (
                <p>Last Updated: {new Date(content.updatedAt).toLocaleString()}</p>
              )}
            </div>

            {/* Approval Information */}
            {content.status === 'APPROVED' && content.approvedBy && (
              <div className="content-section">
                <h4>Approval Information</h4>
                <p>Approved By: User {content.approvedBy}</p>
              </div>
            )}

            {/* Rejection Information */}
            {content.status === 'REJECTED' && (
              <div className="content-section">
                <h4>Rejection Information</h4>
                <p><strong>Status:</strong> Content has been rejected</p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewContentModal; 